import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, StyleSheet, Modal, Dimensions, Platform } from 'react-native';
import { useRouter, useSegments } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/theme';
import { TutorialTooltip } from './TutorialTooltip';
import { TutorialHighlight } from './TutorialHighlight';
import { TUTORIAL_STEPS, type TutorialStep } from '@/data/tutorialSteps';
import { markTutorialComplete } from '@/services/tutorial';
import { useUserStore } from '@/state/userStore';
import { addBreadcrumb } from '@/services/errorTracking';

type PostOnboardingTutorialProps = {
  visible: boolean;
  onComplete: () => void;
};

// Global refs for tutorial targets
const tutorialTargetRefs: Map<string, React.RefObject<View | null>> = new Map();

export function registerTutorialTarget(id: string, ref: React.RefObject<View | null>) {
  tutorialTargetRefs.set(id, ref);
}

export function unregisterTutorialTarget(id: string) {
  tutorialTargetRefs.delete(id);
}

export function PostOnboardingTutorial({ visible, onComplete }: PostOnboardingTutorialProps) {
  const theme = useTheme();
  const router = useRouter();
  const segments = useSegments();
  const insets = useSafeAreaInsets();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const completeTutorial = useUserStore((state) => state.completeTutorial);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetLayout, setTargetLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [tooltipSize, setTooltipSize] = useState<{ width: number; height: number } | null>(null);
  const completionInProgressRef = useRef(false);

  const currentStep: TutorialStep | undefined = TUTORIAL_STEPS[currentStepIndex];
  const isLastStep = currentStepIndex === TUTORIAL_STEPS.length - 1;

  useEffect(() => {
    if (!visible || !currentStep) return;
    addBreadcrumb('Post-onboarding tutorial visible', 'ui.overlay', 'info', {
      stepId: currentStep.id,
      stepIndex: currentStepIndex,
    });
  }, [visible, currentStep, currentStepIndex]);

  // Keep latest segments available to async waiters.
  const segmentsRef = useRef(segments);
  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);

  const parseTabsRouteFromHref = (href: string): { tab: string } | null => {
    // Supports "/(tabs)" (index tab) and "/(tabs)/travel?tab=maps" etc.
    if (href === '/(tabs)' || href.startsWith('/(tabs)?') || href.startsWith('/(tabs)#')) {
      return { tab: 'index' };
    }
    const m = href.match(/^\/\((?:tabs)\)\/([^?/#]+)/);
    if (m?.[1]) return { tab: m[1] };
    return null;
  };

  const getActiveTabFromSegments = (segs: string[]): string | null => {
    if (segs[0] !== '(tabs)') return null;
    // In Expo Router, "(tabs)/index" often reports just ["(tabs)"].
    const leaf = segs[1] ?? 'index';
    return leaf;
  };

  const waitForTab = async (tab: string, timeoutMs = 2000) => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const active = getActiveTabFromSegments(segmentsRef.current as unknown as string[]);
      if (active === tab) return true;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 80));
    }
    return false;
  };

  const computeTooltipPosition = (args: {
    target: { x: number; y: number; width: number; height: number } | null;
    tooltip: { width: number; height: number };
    position: TutorialStep['tooltipPosition'];
  }) => {
    const { target, tooltip, position } = args;
    const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
    const spacing = 16;

    // Default to centered tooltip if no target.
    if (!target) {
      const x = screenWidth / 2 - tooltip.width / 2;
      const y = screenHeight / 2 - tooltip.height / 2;
      return { x, y };
    }

    const { x: tx, y: ty, width: tw, height: th } = target;
    let x = 0;
    let y = 0;

    switch (position) {
      case 'top':
        x = tx + tw / 2 - tooltip.width / 2;
        y = ty - tooltip.height - spacing;
        break;
      case 'bottom':
        x = tx + tw / 2 - tooltip.width / 2;
        y = ty + th + spacing;
        break;
      case 'left':
        x = tx - tooltip.width - spacing;
        y = ty + th / 2 - tooltip.height / 2;
        break;
      case 'right':
        x = tx + tw + spacing;
        y = ty + th / 2 - tooltip.height / 2;
        break;
      default:
        x = tx + tw / 2 - tooltip.width / 2;
        y = ty + th + spacing;
    }

    // Clamp to safe area & viewport.
    const minX = 20;
    const maxX = Math.max(minX, screenWidth - tooltip.width - 20);
    x = Math.max(minX, Math.min(x, maxX));

    const minY = insets.top + 20;
    const maxY = Math.max(minY, screenHeight - tooltip.height - insets.bottom - 20);
    y = Math.max(minY, Math.min(y, maxY));

    return { x, y };
  };

  // Step runner: navigate (if needed) then measure target (with retries).
  useEffect(() => {
    if (!visible || !currentStep) return;
    let cancelled = false;

    const findTarget = async () => {
      // Clear highlight only; keep last tooltip position until recomputed to avoid center↔absolute flicker.
      setTargetLayout(null);

      const goTo = async (href: string) => {
        if (cancelled) return;
        const desired = parseTabsRouteFromHref(href)?.tab;
        router.push(href as any);
        if (desired) {
          const ok = await waitForTab(desired, 2500);
          if (cancelled) return;
          if (!ok) {
            // Retry once — some devices occasionally drop the first tab switch while a Modal is mounted.
            await new Promise((r) => setTimeout(r, 120));
            if (cancelled) return;
            router.push(href as any);
            await waitForTab(desired, 2500);
          }
        } else {
          // Fallback delay for non-tab routes.
          await new Promise((resolve) => setTimeout(resolve, 600));
        }
      };

      // Some steps need to force navigation (e.g. after "More" we want to highlight items back on Trips).
      if (currentStep.navigateTo) {
        await goTo(currentStep.navigateTo);
      }

      // For center tooltips, no target needed
      if (currentStep.target.type === 'none') {
        // Tooltip position will be computed once tooltipSize is known.
        if (!cancelled) {
          setTargetLayout(null);
        }
        return;
      }

      // For tab targets, navigate to the tab first
      if (currentStep.target.type === 'tab') {
        const tabName = currentStep.target.tabName;
        const target = tabName === 'index' ? '/(tabs)' : `/(tabs)/${tabName}`;
        await goTo(target);
      }

      // Find the target element
      let targetRef: React.RefObject<View | null> | null = null;

      if (currentStep.target.type === 'button' || currentStep.target.type === 'element') {
        const targetId =
          currentStep.target.type === 'button'
            ? currentStep.target.buttonId
            : currentStep.target.elementId;
        targetRef = tutorialTargetRefs.get(targetId) || null;
      }

      // Measure target (retry until mounted/layout is ready).
      if (targetRef) {
        for (let i = 0; i < 20; i++) {
          if (cancelled) return;
          if (targetRef.current) {
            const measured = await new Promise<{
              x: number;
              y: number;
              width: number;
              height: number;
            } | null>((resolve) => {
              try {
                targetRef.current?.measureInWindow((x, y, width, height) => {
                  if (width > 0 && height > 0) resolve({ x, y, width, height });
                  else resolve(null);
                });
              } catch {
                resolve(null);
              }
            });
            if (measured) {
              if (!cancelled) {
                setTargetLayout(measured);
              }
              return;
            }
          }
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 80));
        }
      }

      // If we couldn't find/measure the target, fall back to centered tooltip.
      if (!cancelled) {
        setTargetLayout(null);
      }
    };

    const timeout = setTimeout(findTarget, 100);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [visible, currentStepIndex, currentStep, router]);

  // Tooltip position: recompute whenever target or tooltip size changes.
  useEffect(() => {
    if (!visible || !currentStep) return;
    if (!tooltipSize) return;

    const pos = computeTooltipPosition({
      target: currentStep.target.type === 'none' ? null : targetLayout,
      tooltip: tooltipSize,
      position: currentStep.tooltipPosition,
    });
    setTooltipPosition(pos);
  }, [visible, currentStep, targetLayout, tooltipSize, insets.top, insets.bottom]);

  const handleComplete = useCallback(() => {
    if (completionInProgressRef.current) {
      return;
    }
    completionInProgressRef.current = true;
    // Dismiss overlay first so the main UI is interactive before store/network work.
    onComplete();
    completeTutorial();
    if (firebaseUser?.uid) {
      markTutorialComplete(firebaseUser.uid).catch((error) => {
        console.error('[PostOnboardingTutorial] Error marking tutorial complete:', error);
      });
    }
  }, [firebaseUser?.uid, completeTutorial, onComplete]);

  const handleNext = useCallback(() => {
    if (currentStepIndex < TUTORIAL_STEPS.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
      setTargetLayout(null);
      setTooltipPosition(null);
    } else {
      handleComplete();
    }
  }, [currentStepIndex, handleComplete]);

  const handlePrevious = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      setTargetLayout(null);
      setTooltipPosition(null);
    }
  }, [currentStepIndex]);

  useEffect(() => {
    if (!visible) {
      completionInProgressRef.current = false;
    }
  }, [visible]);

  const handleSkip = useCallback(() => {
    handleComplete();
  }, [handleComplete]);

  if (!visible || !currentStep) {
    return null;
  }

  const showHighlight = currentStep.target.type !== 'none' && targetLayout !== null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      statusBarTranslucent
      onRequestClose={handleSkip}
    >
      <View style={styles.container} pointerEvents="box-none">
        {showHighlight && (
          <TutorialHighlight
            targetLayout={targetLayout}
            visible={true}
          />
        )}

        {/* Tooltip */}
        <View
          style={[
            styles.tooltipContainer,
            tooltipPosition && {
              position: 'absolute',
              left: tooltipPosition.x,
              top: tooltipPosition.y,
            },
            !tooltipPosition && styles.centerTooltip,
          ]}
          pointerEvents="box-none"
          onLayout={(event) => {
            const { width, height } = event.nativeEvent.layout;
            if (width > 0 && height > 0) {
              setTooltipSize((prev) => {
                if (prev && prev.width === width && prev.height === height) return prev;
                return { width, height };
              });
            }
          }}
        >
          <TutorialTooltip
            title={currentStep.title}
            description={currentStep.description}
            position={currentStep.tooltipPosition}
            step={currentStepIndex}
            totalSteps={TUTORIAL_STEPS.length}
            onNext={handleNext}
            onPrevious={currentStepIndex > 0 ? handlePrevious : undefined}
            onSkip={handleSkip}
            onGetStarted={isLastStep ? handleComplete : undefined}
            showGetStarted={isLastStep}
            showPrevious={currentStepIndex > 0}
          />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  tooltipContainer: {
    zIndex: 10000,
  },
  centerTooltip: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
});

